/**
 * extras.helpers — the ONE decimal formatter for instruction-option amounts.
 *
 * Shared deliberately by `ExtrasSection` (the price label) and
 * `Instructions.page` (the §11.2 `extrasFromInstructions` re-derive), because
 * `CartExtraSnapshot.unitPrice` is a STRING and `setExtrasFromInstructions`
 * compares snapshots by string equality (`cartSlice.ts:190-202`). Any formatting
 * drift between the two call sites would make the value guard miss and churn the
 * cart-total on every render.
 *
 * Kept pure (no React / store) so both consumers can call it freely.
 */

import Big from 'big.js'

import type { KeyPickupTravelCostRequestV2DTO, KeyPickupTravelCostV2DTO, MeetingDTO, ResolvedInstructionOption } from 'models/purchaseFlowRevamp'
import { ProductKind } from 'constants/product'

import { MEETING_MODE_KIND } from './meeting.helpers'
import { FIXED_DECIMAL_PLACES } from 'utils/price'
import { formatPrice } from 'utils/price/priceFormatter'
import { logger } from 'utils/logging'

import type { CartExtraSnapshot } from '../../store/slices/cartSlice'

/** Shown where an instruction option costs nothing (legacy rendered an `<hr />`). */
export const FREE_OPTION_LABEL = '—'

/**
 * Fixed-decimal string for an option's gross amount. Unpriced options
 * (`amount: undefined`, wire `price: null`) format as zero — the wire says the
 * option costs nothing to add, not that its price is unknown.
 *
 * @example
 * formatInstructionOptionAmount({ amount: 39, … })  // '39.00'
 * formatInstructionOptionAmount({ …no amount… })    // '0.00'
 */
export const formatInstructionOptionAmount = (option: ResolvedInstructionOption): string =>
  new Big(option.amount ?? 0).toFixed(FIXED_DECIMAL_PLACES)

/**
 * Signed, currency-suffixed price label for an instruction option — the
 * `trailing` text shown on a `MUICheckCard` (e.g. `+39.00 EUR`). Built on
 * top of `formatInstructionOptionAmount` so the number itself can never drift
 * from the §11.2 cart-total re-derive in `Instructions.page.tsx`. Shared by
 * `ExtrasSection` (order-level extras) and `MeetingSection` (priced meeting
 * modes, ledger #31) — one formatter, one visual language.
 *
 * @example
 * formatInstructionOptionPrice({ amount: 39, … }, 'EUR')  // '+39.00 EUR'
 * formatInstructionOptionPrice({ amount: 39, … })         // '+39.00'
 */
export const formatInstructionOptionPrice = (option: ResolvedInstructionOption, currency?: string): string => {
  // A free option reads as a dash, not as "+0.00". Legacy rendered an
  // `<hr />` for a zero fee (`InstructionPrice`), and v2's own MeetingSection
  // already suppresses the label via `hasPrice`; only the extras card printed a
  // price of nothing, which reads as a real charge of zero rather than "no fee".
  if (!(option.amount && option.amount > 0)) return FREE_OPTION_LABEL

  const amount = formatInstructionOptionAmount(option)
  // Through the SAME formatter the cart line and the total use. Concatenating
  // the ISO code by hand rendered "+39.00 EUR" on the card beside "39,00 €" in
  // the cart — one amount, one screen, two conventions, and the mismatch was
  // convincing enough to be read as a missing price.
  return currency ? `+${formatPrice(amount, currency)}` : `+${amount}`
}

/* -------------------------------------------------------------------------- *
 *  DF-I24 / R-4 — extras the place-order body can actually carry
 * -------------------------------------------------------------------------- */

/**
 * Order-level extras that survive the trip to the BE.
 *
 * `PlaceOrderBaseV2DTO` has exactly two order-level intent slots — `fastDelivery`
 * and `selectedFavouriteCreativeId` — and the second is fed from `ctBookingId`
 * by its own section, not from `extraServices`. So of everything that can land
 * in the extras bucket, only FAST_DELIVERY has anywhere to go.
 *
 * v1 was strictly more capable here: it sent each selected extra as a real
 * product line in `products[]`. v2 replaced that with server-derived fee
 * products (`FeeProductDeriver`), deliberately, to make FE/BE disagreement
 * impossible — at the cost of a closed set of two.
 *
 * The bucketing routes DELIVERY **and every unrecognised flavor** into extras
 * (`bucketInstructionOptions`), so this list is a real gate, not a formality:
 * without it an unslotted option is offered, priced, added to the displayed
 * total, and then dropped in silence — the client is quoted for something the
 * order never contains. R-4's instruction is explicit: warn and escalate, do
 * NOT invent a wire field.
 *
 * Today the BE emits exactly one DELIVERY kind (FAST_DELIVERY, and only for
 * photo carts), so the gate is inert. It exists so the day a second one appears
 * is a logged warning rather than a mis-quote.
 */
export const WIRE_SLOTTED_EXTRA_KINDS: ReadonlySet<string> = new Set([ProductKind.FAST_DELIVERY])

/** An extras bucket split into what can be ordered and what cannot. */
export interface PartitionedExtras {
  offerable: Partial<Record<ProductKind, ResolvedInstructionOption>>
  /** Kinds present in the catalogue with no place-order slot. Empty in practice. */
  unslotted: ProductKind[]
}

/**
 * Splits the extras bucket by whether the place-order body can carry the kind.
 *
 * @example
 * partitionExtrasByWireSlot({ FAST_DELIVERY: opt, HOUR_ON_SITE: opt })
 * // → { offerable: { FAST_DELIVERY: opt }, unslotted: ['HOUR_ON_SITE'] }
 */
export const partitionExtrasByWireSlot = (
  extras: Partial<Record<ProductKind, ResolvedInstructionOption>>
): PartitionedExtras => {
  const offerable: Partial<Record<ProductKind, ResolvedInstructionOption>> = {}
  const unslotted: ProductKind[] = []

  for (const [kind, option] of Object.entries(extras) as Array<[ProductKind, ResolvedInstructionOption]>) {
    if (WIRE_SLOTTED_EXTRA_KINDS.has(kind)) offerable[kind] = option
    else unslotted.push(kind)
  }

  return { offerable, unslotted }
}

/* -------------------------------------------------------------------------- *
 *  Extras that belong to a paid plan
 * -------------------------------------------------------------------------- */

/**
 * Extras a paid plan includes, and which are therefore NOT sold à la carte.
 *
 * Product rule (2026-08-25): fast delivery is included for a subscribed
 * workspace — shown ticked and not unselectable — and is not offered at all
 * otherwise. The BE gates neither (`ConfigurationService` offers FAST_DELIVERY
 * for any photo cart), so this is a frontend gate, like the measurement
 * narrowing.
 *
 * Consequence worth stating plainly: because the kind is withheld from
 * non-subscribers and never written to the store for subscribers, `fastDelivery`
 * on the place-order body can no longer be true from this flow. Legacy sold it
 * to non-subscribers for its list price alongside a subscribe upsell.
 */
export const SUBSCRIPTION_ONLY_EXTRA_KINDS: ReadonlySet<string> = new Set([ProductKind.FAST_DELIVERY])

/**
 * Drops the plan-only extras — what an unsubscribed workspace may be offered.
 *
 * @example
 * withoutSubscriptionOnlyExtras({ FAST_DELIVERY: opt })  // → {}
 */
export const withoutSubscriptionOnlyExtras = (
  extras: Partial<Record<ProductKind, ResolvedInstructionOption>>
): Partial<Record<ProductKind, ResolvedInstructionOption>> => {
  const out: Partial<Record<ProductKind, ResolvedInstructionOption>> = {}
  for (const [kind, option] of Object.entries(extras) as Array<[ProductKind, ResolvedInstructionOption]>) {
    if (!SUBSCRIPTION_ONLY_EXTRA_KINDS.has(kind)) out[kind] = option
  }
  return out
}

/** The extras the client is offered: those the order can carry, without the paid-plan ones for an unsubscribed workspace. */
export const offerableExtras = (
  extras: Partial<Record<ProductKind, ResolvedInstructionOption>>,
  isSubscribed: boolean
): Partial<Record<ProductKind, ResolvedInstructionOption>> => {
  const { offerable } = partitionExtrasByWireSlot(extras)
  return isSubscribed ? offerable : withoutSubscriptionOnlyExtras(offerable)
}

/* -------------------------------------------------------------------------- *
 *  V-16 — key-pickup travel fee as a display-only cart line
 * -------------------------------------------------------------------------- */

/** Synthetic `CartExtraSnapshot.kind` for the selected organization method's
 *  fee. NOT a `ProductKind`: the fee is a BE-derived order line with no FE-selectable
 *  product, and it must never be mistaken for a selected extra service. */
export const ORGANIZATION_FEE_KIND = 'ORGANIZATION_FEE'

/** Namespaced i18n keys the cart labels the organization-fee line with, per
 *  mode. The KEY, not the translated string, travels through the store so a
 *  language switch relabels the line without a re-derive. Reuses the mode names
 *  the meeting cards already show — the line IS that mode's fee. */
const ORGANIZATION_FEE_LABEL_KEY: Record<'ON_SITE' | 'KEY_PICKUP' | 'THIRD_PARTY', string> = {
  ON_SITE: 'purchase_flow_revamp:instructions.meeting.mode_on_site',
  KEY_PICKUP: 'purchase_flow_revamp:instructions.meeting.mode_key_pickup',
  THIRD_PARTY: 'purchase_flow_revamp:instructions.meeting.mode_third_party',
}

/**
 * The display-only cart line for the SELECTED organization method's own
 * fee.
 *
 * Legacy pushed the chosen organization instruction into the cart as a line
 * (`Cart.context.tsx`), so a priced access method was part of the total the
 * client agreed to. v2 fed its mirror only from `extraServices` plus the two
 * synthetic travel fees, so a priced `MEETING_ON_SITE` or
 * `ORGANIZATION_THIRD_PARTY` advertised `+X` on its card and was then absent
 * from the total.
 *
 * Replaces the previous V-16 key-pickup-only line, which mirrored THIS amount —
 * the organization fee — while labelling it the *travel* fee. That number was
 * never the travel cost: the travel fee is BE-derived at placement and its
 * amount is not on the wire at all (which is why the KEY_PICKUP card discloses
 * it in words). So key pickup's total does not change here, only its label
 * becomes true, and the other two modes gain the fee they always advertised.
 *
 * Still display-only (MODULE-CONTRACT §11.2): absent from `serializeDraft` and
 * the autosave fingerprint, never read by `serializePlacement`. The BE derives
 * the real order line from the organization method; sending it ourselves would
 * double-charge.
 *
 * Returns `null` when no mode is selected, when the catalogue carries no option
 * for it, or when that option is worth nothing.
 *
 * @example
 * resolveOrganizationFee({ mode: 'ON_SITE' }, { MEETING_ON_SITE: option25 })
 * // { kind: 'ORGANIZATION_FEE', unitPrice: '25.00', labelKey: '…mode_on_site' }
 */
export const resolveOrganizationFee = (
  meeting: MeetingDTO | undefined,
  organizationInstructions: Partial<Record<ProductKind, ResolvedInstructionOption>>
): CartExtraSnapshot | null => {
  const mode = meeting?.mode
  if (!mode || mode === 'NONE') return null

  const kind = MEETING_MODE_KIND[mode]
  const option = kind ? organizationInstructions[kind] : undefined
  if (!option) return null

  const unitPrice = formatInstructionOptionAmount(option)
  if (new Big(unitPrice).lte(0)) return null

  return {
    kind: ORGANIZATION_FEE_KIND,
    unitPrice,
    labelKey: ORGANIZATION_FEE_LABEL_KEY[mode],
    // Display ignores it; the payment intent charges from it. Key pickup's travel
    // cost is its own quoted line (`resolveKeyPickupTravelFee`), not carried here.
    productCode: option.productCode,
  }
}

/** Synthetic `CartExtraSnapshot.kind` for the key-pickup travel cost, a BE-derived order line. */
export const KEY_PICKUP_TRAVEL_FEE_KIND = 'KEYS_PICKUP_TRAVEL_FEE'

export const KEY_PICKUP_TRAVEL_FEE_LABEL_KEY = 'purchase_flow_revamp:instructions.meeting.key_pickup_travel_cost_label'

/** The quote request for the chosen key pickup, or null when there is no geocoded pickup to quote. */
export const keyPickupTravelQuoteRequest = (meeting: MeetingDTO | undefined): KeyPickupTravelCostRequestV2DTO | null => {
  if (meeting?.mode !== 'KEY_PICKUP') return null
  const { address, coordinates } = meeting.keyPickup ?? {}
  if (!address || !coordinates) return null
  return { address, coordinates }
}

/**
 * The key-pickup travel cost as a cart line: the quoted total on screen, and its distance units as the
 * payment intent's count. Placement derives the same line itself, so it is never sent to place-order.
 * Null without a quote, and for a pickup at the property (zero units).
 */
export const resolveKeyPickupTravelFee = (
  meeting: MeetingDTO | undefined,
  quote: KeyPickupTravelCostV2DTO | undefined
): CartExtraSnapshot | null => {
  if (meeting?.mode !== 'KEY_PICKUP' || !quote || quote.quantity <= 0) return null
  return {
    kind: KEY_PICKUP_TRAVEL_FEE_KIND,
    unitPrice: new Big(quote.total.amount).toFixed(FIXED_DECIMAL_PLACES),
    labelKey: KEY_PICKUP_TRAVEL_FEE_LABEL_KEY,
    productCode: quote.productCode,
    count: quote.quantity,
  }
}

/** Synthetic `CartExtraSnapshot.kind` for the arranging fee of a flexible key pickup. */
export const SCHEDULING_FEE_KIND = 'SCHEDULING_FEE'

/**
 * The third-party organization fee a flexible key pickup carries on top of the
 * pickup itself: the BE bills both, because the creative collects the keys AND
 * arranges the time. Null for every other meeting, and — like every
 * organization fee — when the option is free.
 */
export const resolveSchedulingFee = (
  meeting: MeetingDTO | undefined,
  organizationInstructions: Partial<Record<ProductKind, ResolvedInstructionOption>>
): CartExtraSnapshot | null => {
  if (meeting?.mode !== 'KEY_PICKUP' || !meeting.keyPickup?.arrangedByCreative) return null
  const fee = resolveOrganizationFee({ mode: 'THIRD_PARTY' }, organizationInstructions)
  return fee && { ...fee, kind: SCHEDULING_FEE_KIND }
}

/* -------------------------------------------------------------------------- *
 *  Favourite-creative travel fee as a display-only cart line
 * -------------------------------------------------------------------------- */

/** Synthetic `CartExtraSnapshot.kind` for the favourite-creative travel fee.
 *  NOT a `ProductKind`, for the same reason as its key-pickup sibling: the fee
 *  is a BE-derived order line, not a selectable extra service. */
export const FAVOURITE_CREATIVE_TRAVEL_FEE_KIND = 'FAVOURITE_CREATIVE_TRAVEL_FEE'

/** Namespaced i18n key the cart labels the fee line with. The KEY, not the
 *  translated string, travels through the store so a language switch relabels
 *  the line without a re-derive. */
export const FAVOURITE_CREATIVE_TRAVEL_FEE_LABEL_KEY =
  'purchase_flow_revamp:instructions.ct_booking.travel_cost_label'

/** The shape this resolver needs off a listed creative — structural on purpose,
 *  so the helper stays free of the data-query layer it is fed from. */
export interface CreativeTravelFeeSource {
  id: string
  travelCost?: { amount: number, productCode?: string, quantity?: number }
}

/**
 * The display-only cart line for booking a specific creative.
 *
 * Booking a favourite is not free: the BE appends a per-country travel-cost
 * product to the order whenever `selectedFavouriteCreativeId` is set
 * (`FeeProductDeriver.travelCostProducts`), priced by the driving distance from
 * THAT creative to the property — which is why the amount comes off the selected
 * creative rather than off a catalogue option, unlike every other fee here.
 *
 * Mirrored exactly like the key-pickup fee (MODULE-CONTRACT §11.2): a
 * `CartExtraSnapshot`, display-only — absent from `serializeDraft` and the
 * autosave fingerprint, never read by `serializePlacement`. The BE derives the
 * real line from the selected creative; sending it ourselves would double-charge.
 *
 * Returns `null` when nothing is selected, when the selection is not (yet) in the
 * listed set, or when the quoted travel cost is worth nothing — a creative whose
 * distance rounds to zero adds no line rather than a `0.00` one.
 *
 * @example
 * resolveFavouriteCreativeTravelFee('ct-1', [{ id: 'ct-1', travelCost: { amount: 12 } }])
 * // { kind: 'FAVOURITE_CREATIVE_TRAVEL_FEE', unitPrice: '12.00', labelKey: '…' }
 */
export const resolveFavouriteCreativeTravelFee = (
  selectedCreativeId: string | undefined,
  creatives: ReadonlyArray<CreativeTravelFeeSource>
): CartExtraSnapshot | null => {
  if (!selectedCreativeId) return null

  const selected = creatives.find((creative) => creative.id === selectedCreativeId)
  if (selected?.travelCost === undefined) return null

  const unitPrice = new Big(selected.travelCost.amount).toFixed(FIXED_DECIMAL_PLACES)
  if (new Big(unitPrice).lte(0)) return null
  // The payment intent charges `count` units and falls back to one; a priced fee without its units would collect
  // a fraction of what the order bills (QA-654).
  const { quantity } = selected.travelCost
  if (quantity === undefined || quantity <= 0) {
    logger.warn('PF revamp: favourite creative travel cost has no units, the payment intent would undercharge', undefined, {
      context: { component: 'extras.helpers', action: 'resolve_creative_travel_fee', metadata: { productCode: selected.travelCost.productCode, amount: unitPrice, quantity } },
    })
  }

  return {
    kind: FAVOURITE_CREATIVE_TRAVEL_FEE_KIND,
    unitPrice,
    labelKey: FAVOURITE_CREATIVE_TRAVEL_FEE_LABEL_KEY,
    // `CreativeTravelCostV2DTO.productCode` exists for exactly this: the order
    // line is derived server-side, so the code's only job is to mirror the same
    // charge into the payment intent.
    productCode: selected.travelCost.productCode,
    // The product is priced per kilometre: the payment intent charges these units, not one.
    count: quantity,
  }
}

/* -------------------------------------------------------------------------- *
 *  Client-selected extra services as display-only cart lines
 * -------------------------------------------------------------------------- */

/**
 * Snapshots for the extras the client ticked in `ExtrasSection` (today: fast
 * delivery, favourite creative).
 *
 * Extracted from `Instructions.page`'s assembly effect so the invariant below
 * is testable on its own: every snapshot that contributes to the cart total
 * must also be able to name itself, or the total moves with nothing on screen
 * to explain it. `useCartDisplay` itemises a snapshot only when it carries a
 * `labelKey`.
 *
 * The key matches the one `ExtrasSection` labels the checkbox with, so the
 * cart line and the control that produced it always read the same.
 *
 * @example
 * resolveExtraServiceFees(['FAST_DELIVERY'], { FAST_DELIVERY: option29 })
 * // [{ kind: 'FAST_DELIVERY', unitPrice: '29.00', labelKey: '…extras.kind.FAST_DELIVERY' }]
 */
export const extraServiceLabelKey = (kind: string) =>
  `purchase_flow_revamp:instructions.extras.kind.${kind}`

export const resolveExtraServiceFees = (
  selectedKinds: ReadonlyArray<string>,
  extrasCatalogue: Partial<Record<ProductKind, ResolvedInstructionOption>>
): CartExtraSnapshot[] => {
  const snapshots: CartExtraSnapshot[] = []

  for (const kind of selectedKinds) {
    const option = extrasCatalogue[kind as ProductKind]
    if (!option) continue

    snapshots.push({
      kind,
      // MUST use the same formatter `ExtrasSection` labels with — the snapshot's
      // `unitPrice` is a string compared by equality in the setter's value guard.
      unitPrice: formatInstructionOptionAmount(option),
      labelKey: extraServiceLabelKey(kind),
      productCode: option.productCode,
      impliedFeeProductCode: option.impliedFeeProductCode,
    })
  }

  return snapshots
}
